import { OPTION_LIST } from '@/constants/context';
import { useQueryGetConjunctions } from '@/query/conjunctions/useQueryCdm';
import { useCallback, useEffect, useState } from 'react';
import { convertTableData } from './conjunctionTableUtils';
import { useCollectionDateParse } from '@/hooks/conjunctions/useCollectionDateParse';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

const MONTH_MAP: Record<string, string> = {
  JAN: '01',
  FEB: '02',
  MAR: '03',
  APR: '04',
  MAY: '05',
  JUN: '06',
  JUL: '07',
  AUG: '08',
  SEP: '09',
  OCT: '10',
  NOV: '11',
  DEC: '12',
};

dayjs.extend(customParseFormat);

const useConjunctionTable = ({ limit, queryKey }) => {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(OPTION_LIST[3].value);

  useEffect(() => {
    setPage(0);
  }, [selected]);

  const isGlobal = selected === OPTION_LIST[0].value;
  const isCountry =
    selected === OPTION_LIST[1].value ||
    selected === OPTION_LIST[2].value ||
    selected === OPTION_LIST[3].value;
  const ids =
    selected === OPTION_LIST[6].value
      ? [
          66650, 66651, 66652, 66653, 66654, 66655, 66656, 66657, 66658, 66659,
          66660, 66661, 66662, 66663,
        ]
      : undefined;

  const params = {
    option: 'simple',
    log: 'www',
    limit,
    page,
    ...(isCountry
      ? { country: selected }
      : isGlobal
        ? {}
        : { primary: selected }),
  };

  const { data, isLoading, isFetching } = useQueryGetConjunctions(
    selected === OPTION_LIST[6].value ? { ...params, ids } : params,
    queryKey,
    'recent'
  );

  const tableData = data ? convertTableData(data) : [];

  const rawCollectionId = data?.conjunctions?.[0]?.MESSAGE_ID ?? '';
  const transformedCollectionId = rawCollectionId.includes('_S1')
    ? `${rawCollectionId.split('_S1')[0]}_S1`
    : rawCollectionId;

  const formatDateString = useCallback((input: string) => {
    if (!input || input.length < 14) {
      return null;
    }
    const monthAbbr = input.substring(4, 7).toUpperCase();

    if (!MONTH_MAP[monthAbbr]) {
      return null;
    }

    const normalizedInput = input.replace(monthAbbr, MONTH_MAP[monthAbbr]);

    const parsed = dayjs(normalizedInput, 'YYYYMMDD[T]HHmm');

    if (!parsed.isValid()) {
      return null;
    }

    return parsed.format('YYYY-MM-DD HH:mm');
  }, []);

  const startCollection = formatDateString(
    transformedCollectionId.split('_')[2]
  );
  const endCollection = dayjs(startCollection)
    .add(3, 'day')
    .format('YYYY-MM-DD HH:mm');

  const cleanMessageId = data?.messageId
    ? data.messageId.replace('SPACEMAP_CA_', '').split('_')[0]
    : '';
  const { startDate, endDate } = useCollectionDateParse(cleanMessageId);

  return {
    selected,
    setSelected,
    page,
    setPage,
    isLoading,
    isFetching,
    tableData,
    messageId: cleanMessageId,
    startDate,
    endDate,
    startCollection,
    endCollection,
  };
};

export { useConjunctionTable };
